import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../components/layout/Layout";
import "./screen.css";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import { deleteScreen, addScreen, getScreens, screenFlagOff, addPlaylistToScreen, assignDevice } from "../../Redux/Actions/screenAction";
import { getStores, addStore } from "../../Redux/Actions/storeAction";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as AddScreenIcon } from "../../Assets/images/addScreenIcon.svg";
import PrimarySearchBar from "../../components/searchBar/searchbar";
import DeleteModel from "../../components/deletemodel/DeleteModel";
import { getPlaylist, addPlaylist } from "../../Redux/Actions/playlistAction";
import { getContent, addContent } from "../../Redux/Actions/contentAction";
import toast from "react-hot-toast";
import { api } from "../../api/api";

const CustomTableCell = styled(TableCell)(() => ({
  color: '#15192C',
  fontSize: '14px',
  fontWeight: '700',
}));

function Screen({ socketRef, user }) {
  const isAdmin = user?.role === 'admin'; // WG 동기화·OTA·Log 는 admin 전용 (/peer/* 서버도 admin 만)
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { screens, isAdded, isScreenDeleted, mac, screenLoading, error } = useSelector(
    (state) => state.Screen
  );
  const { playlist } = useSelector((state) => state.Playlist);
  const { stores } = useSelector((state) => state.Store);
  const [openModel, setOpenModel] = useState(false);
  const [screenId, setScreenId] = useState({ id: '', mac: '' });
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const macRef = useRef();
  const [textVal, setTextVal] = useState(() => sessionStorage.getItem('screenFilter') || '');
  const [filteredScreen, setFilteredScreen] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [bulkPlaylistId, setBulkPlaylistId] = useState("");
  const [playlistSearch, setPlaylistSearch] = useState("");
  const [playlistSort, setPlaylistSort] = useState("name-asc");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [openAssignDevice, setOpenAssignDevice] = useState(false);
  const [assignTargetScreen, setAssignTargetScreen] = useState(null);
  const [deviceCode, setDeviceCode] = useState("");
  /** 플레이리스트 일괄 변경: 체크 시에만 행 체크박스·일괄 툴바 표시 */
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  /** 퀵 스토어 등록 */
  const [quickStoreMode, setQuickStoreMode] = useState(false);
  const [quickStoreName, setQuickStoreName] = useState('');
  const [quickStoreTljNo, setQuickStoreTljNo] = useState('');
  const [quickStoreState, setQuickStoreState] = useState('');
  const [quickStoreTown, setQuickStoreTown] = useState('');
  const [quickStoreAddress, setQuickStoreAddress] = useState('');
  const [quickStoreScreenCount, setQuickStoreScreenCount] = useState(1);
  /** 퀵 셋업 스텝 (0: 스크린 기본, 1: 퀵스토어, 2: 플레이리스트, 3: 콘텐츠) */
  const [quickStep, setQuickStep] = useState(0);
  const [quickPlaylistName, setQuickPlaylistName] = useState('');
  const [quickContentFiles, setQuickContentFiles] = useState([]);
  const [quickCreatedStoreId, setQuickCreatedStoreId] = useState('');
  const [quickCreatedPlaylistId, setQuickCreatedPlaylistId] = useState('');
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const quickFileRef = useRef(null);

  // OTA Update
  const [otaDialog, setOtaDialog] = useState(false);
  const [otaTarget, setOtaTarget] = useState(null); // single device or 'all'
  const [otaSending, setOtaSending] = useState(false);
  const [otaHistory, setOtaHistory] = useState([]);
  const [otaHistoryDialog, setOtaHistoryDialog] = useState(false);
  const [otaHistoryCode, setOtaHistoryCode] = useState('');

  const sendOtaUpdate = async (deviceCodes) => {
    setOtaSending(true);
    try {
      const apkUrl = 'https://tools.digntec.com/deploy-assets/apks/signage.apk';
      const res = await api.post('/peer/command/broadcast', {
        deviceCodes,
        command: 'update_apk',
        payload: { url: apkUrl, version: '2.13' },
        expiresInMs: 24 * 60 * 60 * 1000,
      });
      if (res.data?.success) {
        toast.success(`OTA sent to ${res.data.sent} device(s)`);
      } else {
        toast.error('OTA send failed');
      }
    } catch (e) {
      toast.error('OTA error: ' + (e.response?.data?.message || e.message));
    }
    setOtaSending(false);
    setOtaDialog(false);
  };

  const loadOtaHistory = async (code) => {
    try {
      const res = await api.get(`/peer/command/history/${code}?limit=20`);
      setOtaHistory(res.data?.commands || []);
      setOtaHistoryCode(code);
      setOtaHistoryDialog(true);
    } catch (e) {
      toast.error('Failed to load history');
    }
  };

  const onMultiSelectModeChange = useCallback((e) => {
    const on = e.target.checked;
    setMultiSelectMode(on);
    if (!on) setCheckedIds(new Set());
  }, []);

  useEffect(() => {
    dispatch(getPlaylist(user?._id));
    dispatch(getScreens(user?._id));
    dispatch(getContent(user?._id));
    dispatch(getStores(user?._id));
  }, []);

  useEffect(() => {
    if (screens.length === 0) return;
    const filterParam = new URLSearchParams(location.search).get('filter');
    if (filterParam === 'offline') {
      setFilteredScreen(screens.filter(s => s.status === false));
      return;
    }
    const saved = sessionStorage.getItem('screenFilter');
    if (saved && saved !== '') {
      setFilteredScreen(screens.filter(s =>
        Object.values(s).join('').toLocaleLowerCase().includes(saved.toLocaleLowerCase())
      ));
    } else {
      setFilteredScreen([...screens]);
    }
  }, [screens, location.search]);

  useEffect(() => {
    if (isAdded) {
      const saved = sessionStorage.getItem('screenFilter') || '';
      setFilteredScreen(saved
        ? screens.filter(s => Object.values(s).join('').toLocaleLowerCase().includes(saved.toLocaleLowerCase()))
        : [...screens]
      );
      dispatch(screenFlagOff());
    }
    if (isScreenDeleted) {
      setFilteredScreen([...screens]);
      dispatch(screenFlagOff());
    }
  }, [isAdded, isScreenDeleted]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(+e.target.value); setPage(0); };

  const emitOnDeleteScreen = (mac) => {
    const id = setTimeout(() => { socketRef.current?.emit("screen_deleted", { detail: { mac } }); clearTimeout(id); }, 2000);
  };
  const deleteScreenHandler = (id, mac) => { setScreenId({ id, mac }); setOpenDeleteDialog(true); };
  const confirmDeleteScreen = () => { dispatch(deleteScreen(screenId.id)); emitOnDeleteScreen(screenId.mac); setOpenDeleteDialog(false); };

  const emitOnAddedScreen = () => {
    const id = setTimeout(() => { socketRef.current?.emit("screen_added", { detail: { mac: macRef.current.value, code } }); clearTimeout(id); }, 2000);
  };
  const submit = () => {
    dispatch(addScreen({ name, user: user?._id, subuser: user?._id, role: user?.role, storeId: selectedStoreId || undefined }, code));
    setOpenModel(false);
    emitOnAddedScreen();
    setCode(""); setName(""); setSelectedStoreId("");
  };

  const submitAssignDevice = () => {
    if (!deviceCode.trim() || !assignTargetScreen) return;
    dispatch(assignDevice(assignTargetScreen._id, deviceCode.trim()));
    setOpenAssignDevice(false);
    setDeviceCode("");
    setAssignTargetScreen(null);
  };

  const searchHandler = (e) => {
    setTextVal(e);
    sessionStorage.setItem('screenFilter', e);
    setFilteredScreen(e !== ''
      ? screens.filter(s => Object.values(s).join('').toLocaleLowerCase().includes(e.toLocaleLowerCase()))
      : [...screens]
    );
  };
  const resetFilter = () => { setTextVal(''); sessionStorage.removeItem('screenFilter'); setFilteredScreen([...screens]); };

  const toggleCheck = (id) => {
    setCheckedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleCheckAll = () => {
    const pageIds = filteredScreen.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(s => s._id);
    const allChecked = pageIds.every(id => checkedIds.has(id));
    setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => allChecked ? n.delete(id) : n.add(id)); return n; });
  };
  const applyBulkPlaylist = () => {
    if (!bulkPlaylistId) return;
    checkedIds.forEach(id => {
      dispatch(addPlaylistToScreen({ id, value: bulkPlaylistId }));
      const screen = screens.find(s => s._id === id);
      if (screen) { const t = setTimeout(() => { socketRef.current?.emit("playlist_added", { detail: { mac: screen.mac, code: id } }); clearTimeout(t); }, 2000); }
    });
    setCheckedIds(new Set()); setBulkPlaylistId(""); setOpenBulkDialog(false);
  };
  const navigateToSetting = (id) => {
    // 캐러셀(이전/다음)용 전체 ID — screenFilteredIds는 현재 스크린만 (일괄 배지 등에서 length 오용 방지)
    sessionStorage.setItem('screenSettingCarouselIds', JSON.stringify(filteredScreen.map((s) => s._id)));
    sessionStorage.setItem('screenFilteredIds', JSON.stringify([id]));
    navigate(`/setting/${id}`);
  };

  const pagedScreens = filteredScreen.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // 플레이리스트 필터/정렬
  const getFilteredPlaylists = () => {
    const vp = (playlist || []).filter(p => p.content && p.content.some(c => c.type === 'video'));
    const searched = vp.filter(p => p.name?.toLowerCase().includes(playlistSearch.toLowerCase()));
    return [...searched].sort((a, b) => {
      if (playlistSort === 'name-asc') return (a.name || '').localeCompare(b.name || '');
      if (playlistSort === 'name-desc') return (b.name || '').localeCompare(a.name || '');
      if (playlistSort === 'date-desc') return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
      return new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0);
    });
  };

  return (
    <>
      <Layout title={"Screen"} user={user}>
        <input ref={macRef} hidden value={mac} />

        {/* 검색바 + 버튼 (공통) */}
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 160, background: 'white', borderRadius: '12px', px: 1 }}>
            <PrimarySearchBar value={textVal} onChange={(e) => searchHandler(e.target.value)} />
          </Box>
          {textVal && (
            <button onClick={resetFilter} style={{ height: 40, background: 'none', border: '1px solid #ccc', borderRadius: '8px', padding: '0 14px', cursor: 'pointer', color: '#888', fontSize: '13px' }}>
              초기화
            </button>
          )}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 40,
              padding: '0 12px',
              background: multiSelectMode ? 'rgba(85,31,255,0.08)' : 'white',
              border: multiSelectMode ? '1px solid #551FFF' : '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              color: multiSelectMode ? '#551FFF' : '#555',
              userSelect: 'none',
              flexShrink: 0,
            }}
          >
            <input type="checkbox" checked={multiSelectMode} onChange={onMultiSelectModeChange} style={{ cursor: 'pointer', width: 16, height: 16 }} />
            일괄 플레이리스트 변경
          </label>
          {isAdmin && <button
            style={{ height: 40, padding: '0 14px', borderRadius: '8px', flexShrink: 0, background: 'white', border: '1px solid #22c55e', color: '#22c55e', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={async () => {
              try {
                const { data } = await require('../../api/api').api.post('/peer/sync-wg');
                const msg = `WG 동기화: ${data.synced}개 연결, ${data.already}개 기존, 피어 ${data.peers}개`;
                alert(msg);
                dispatch(getScreens(user?._id || user?.user?._id));
              } catch (e) { alert('WG 동기화 실패: ' + (e.response?.data?.message || e.message)); }
            }}
          >
            WG 동기화
          </button>}
          <button className="add-screen-btn" style={{ height: 40, width: 'auto', padding: '0 20px', borderRadius: '8px', flexShrink: 0 }} onClick={() => setOpenModel(true)}>
            + Add Screen
          </button>
        </Box>

        {/* 일괄 선택 툴바 — 다중 선택 모드에서만 */}
        {multiSelectMode && checkedIds.size > 0 && (
          <Box sx={{ mx: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, background: 'rgba(85,31,255,0.05)', borderRadius: '8px' }}>
            <span style={{ fontSize: '13px', color: '#551FFF' }}>{checkedIds.size}개 선택됨</span>
            <button onClick={() => setOpenBulkDialog(true)} style={{ background: '#551FFF', color: 'white', border: 'none', borderRadius: '6px', padding: '5px 14px', fontSize: '12px', cursor: 'pointer' }}>
              플레이리스트 변경
            </button>
            <button onClick={() => setCheckedIds(new Set())} style={{ background: 'none', color: '#888', border: '1px solid #ccc', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}>
              취소
            </button>
          </Box>
        )}

        {/* ===== 데스크탑: 테이블 레이아웃 ===== */}
        <Box className="screen-desktop-view" sx={{ mx: 2, background: 'white', borderRadius: '16px', overflow: 'hidden' }}>
          <div className="screen-table">
            <Table>
              <TableHead>
                <TableRow style={{ borderBottom: "2px solid #551fff" }}>
                  {multiSelectMode && (
                    <CustomTableCell align="center" padding="checkbox">
                      <input type="checkbox" style={{ cursor: 'pointer' }}
                        onChange={toggleCheckAll}
                        checked={pagedScreens.length > 0 && pagedScreens.every(s => checkedIds.has(s._id))}
                      />
                    </CustomTableCell>
                  )}
                  <CustomTableCell align="center">No.</CustomTableCell>
                  <CustomTableCell align="center">Code</CustomTableCell>
                  <CustomTableCell align="center">Name</CustomTableCell>
                  <CustomTableCell align="center">MAC</CustomTableCell>
                  <CustomTableCell align="center">Playlist</CustomTableCell>
                  <CustomTableCell align="center">Status</CustomTableCell>
                  <CustomTableCell align="center">WG</CustomTableCell>
                  <CustomTableCell align="center">Provisioning</CustomTableCell>
                  <CustomTableCell align="center">Actions</CustomTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {screenLoading ? (
                  <TableRow><TableCell colSpan={multiSelectMode ? 10 : 9} align="center">
                    <img src="/images/spiner.gif" style={{ height: 100, width: 100 }} />
                  </TableCell></TableRow>
                ) : pagedScreens.map((screen, index) => (
                  <TableRow key={screen._id} selected={multiSelectMode && checkedIds.has(screen._id)} hover>
                    {multiSelectMode && (
                      <TableCell align="center" padding="checkbox">
                        <input type="checkbox" style={{ cursor: 'pointer' }} checked={checkedIds.has(screen._id)} onChange={() => toggleCheck(screen._id)} />
                      </TableCell>
                    )}
                    <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell align="center">{screen.code}</TableCell>
                    <TableCell align="center">{screen.name}</TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{screen.mac}</TableCell>
                    <TableCell align="center">
                      <span style={{ fontSize: '12px', color: screen.playlist?.name ? '#551FFF' : '#aaa' }}>
                        {screen.playlist?.name || '-'}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      <div className={`status-badge ${screen.status ? 'success' : 'danger'}`} />
                    </TableCell>
                    <TableCell align="center">
                      {screen.wgStatus === 'connected' ? (
                        <span style={{ color: '#22c55e', fontSize: '12px', fontWeight: 600 }}>● Connected</span>
                      ) : screen.wgStatus === 'disconnected' ? (
                        <span style={{ color: '#f59e0b', fontSize: '12px', fontWeight: 600 }}>● Disconnected</span>
                      ) : (
                        <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>
                      )}
                      {screen.wgIp && <div style={{ fontSize: '10px', color: '#aaa', fontFamily: 'monospace' }}>{screen.wgIp}</div>}
                    </TableCell>
                    <TableCell align="center">
                      {screen.provisioningStatus === 'active' ? (
                        <span style={{ background: '#f0fdf4', color: '#16a34a', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>Active</span>
                      ) : screen.provisioningStatus === 'app_installed' ? (
                        <span style={{ background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>App OK</span>
                      ) : screen.provisioningStatus === 'wg_configured' ? (
                        <span style={{ background: '#fffbeb', color: '#d97706', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>WG Only</span>
                      ) : (
                        <span style={{ background: '#f5f5f5', color: '#999', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>Pending</span>
                      )}
                      {screen.appVersion && <div style={{ fontSize: '10px', color: '#aaa', marginTop: 2 }}>v{screen.appVersion}</div>}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {screen.mac?.startsWith('PLH_') && (
                          <button onClick={() => { setAssignTargetScreen(screen); setOpenAssignDevice(true); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F7A800', display: 'flex', alignItems: 'center', gap: 2, fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(247,168,0,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                            📱 기기 연결
                          </button>
                        )}
                        <button onClick={() => navigateToSetting(screen._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#551FFF', display: 'flex', alignItems: 'center', gap: 2, fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(85,31,255,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                          <SettingsIcon style={{ fontSize: 16 }} /> 설정
                        </button>
                        <button onClick={() => deleteScreenHandler(screen._id, screen.mac)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#FF3B3B', display: 'flex', alignItems: 'center', gap: 2, fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,59,59,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                          <DeleteOutlineIcon style={{ fontSize: 16 }} /> 삭제
                        </button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, { label: `전체 (${filteredScreen.length})`, value: filteredScreen.length }]}
              component="div"
              count={filteredScreen.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </Box>

        {/* ===== 모바일: 카드 레이아웃 ===== */}
        <Box className="screen-mobile-view" sx={{ px: 2, pb: 2 }}>
          {screenLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <img src="/images/spiner.gif" style={{ height: 80, width: 80 }} />
            </Box>
          ) : filteredScreen.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: '#aaa', fontSize: '14px' }}>스크린이 없습니다.</Box>
          ) : (
            <>
              {multiSelectMode && (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <label style={{ fontSize: '12px', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="checkbox"
                      onChange={() => {
                        const allChecked = filteredScreen.every(s => checkedIds.has(s._id));
                        setCheckedIds(prev => { const n = new Set(prev); filteredScreen.forEach(s => allChecked ? n.delete(s._id) : n.add(s._id)); return n; });
                      }}
                      checked={filteredScreen.length > 0 && filteredScreen.every(s => checkedIds.has(s._id))}
                      style={{ cursor: 'pointer' }}
                    />
                    전체 선택
                  </label>
                  <span style={{ fontSize: '12px', color: '#aaa' }}>총 {filteredScreen.length}개</span>
                </Box>
              )}
              <div className="screen-cards-grid">
                {filteredScreen.map((screen) => (
                  <div key={screen._id} className={`screen-card ${multiSelectMode && checkedIds.has(screen._id) ? 'screen-card--checked' : ''}`}>
                    <div className="screen-card-header">
                      {multiSelectMode && (
                        <input type="checkbox" checked={checkedIds.has(screen._id)} onChange={() => toggleCheck(screen._id)}
                          style={{ cursor: 'pointer', flexShrink: 0 }} onClick={e => e.stopPropagation()} />
                      )}
                      <DesktopMacIcon style={{ color: '#551FFF', fontSize: 20 }} />
                      <span className="screen-card-name">{screen.name || '-'}</span>
                      <div className={`status-badge ${screen.status ? 'success' : 'danger'}`} style={{ marginLeft: 'auto', flexShrink: 0 }} />
                    </div>
                    <div className="screen-card-body" onClick={() => navigateToSetting(screen._id)}>
                      <div className="screen-card-row">
                        <span className="screen-card-label">Code</span>
                        <span className="screen-card-value">{screen.code || '-'}</span>
                      </div>
                      <div className="screen-card-row">
                        <span className="screen-card-label">MAC</span>
                        <span className="screen-card-value screen-card-mono">{screen.mac || '-'}</span>
                      </div>
                      <div className="screen-card-row">
                        <span className="screen-card-label">Playlist</span>
                        <span className="screen-card-value" style={{ color: screen.playlist?.name ? '#551FFF' : '#ccc' }}>
                          {screen.playlist?.name || '미지정'}
                        </span>
                      </div>
                      <div className="screen-card-row">
                        <span className="screen-card-label">WG</span>
                        <span className="screen-card-value" style={{ color: screen.wgStatus === 'connected' ? '#22c55e' : screen.wgStatus === 'disconnected' ? '#f59e0b' : '#ccc' }}>
                          {screen.wgStatus === 'connected' ? '● Connected' : screen.wgStatus === 'disconnected' ? '● Disconnected' : '—'}
                          {screen.wgIp ? ` (${screen.wgIp})` : ''}
                        </span>
                      </div>
                      <div className="screen-card-row">
                        <span className="screen-card-label">Status</span>
                        <span className="screen-card-value">
                          {screen.provisioningStatus === 'active' ? '✅ Active' : screen.provisioningStatus === 'app_installed' ? '📱 App OK' : screen.provisioningStatus === 'wg_configured' ? '🔐 WG Only' : '⏳ Pending'}
                          {screen.appVersion ? ` v${screen.appVersion}` : ''}
                        </span>
                      </div>
                    </div>
                    <div className="screen-card-actions">
                      {screen.mac?.startsWith('PLH_') && (
                        <button className="screen-card-btn" onClick={() => { setAssignTargetScreen(screen); setOpenAssignDevice(true); }}
                          style={{ color: '#F7A800', border: '1px solid rgba(247,168,0,0.4)', background: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                          📱 기기 연결
                        </button>
                      )}
                      {isAdmin && <>
                      <button className="screen-card-btn" onClick={() => { setOtaTarget(screen); setOtaDialog(true); }}
                        style={{ color: '#2196F3', border: '1px solid rgba(33,150,243,0.4)', background: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer' }}>
                        OTA
                      </button>
                      <button className="screen-card-btn" onClick={() => loadOtaHistory(screen.mac || screen.code)}
                        style={{ color: '#9C27B0', border: '1px solid rgba(156,39,176,0.3)', background: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer' }}>
                        Log
                      </button>
                      </>}
                      <button className="screen-card-btn screen-card-btn--settings" onClick={() => navigateToSetting(screen._id)}>
                        <SettingsIcon style={{ fontSize: 16 }} /> 설정
                      </button>
                      <button className="screen-card-btn screen-card-btn--delete" onClick={() => deleteScreenHandler(screen._id, screen.mac)}>
                        <DeleteOutlineIcon style={{ fontSize: 16 }} /> 삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Box>
      </Layout>

      <DeleteModel open={openDeleteDialog} onClose={(e) => setOpenDeleteDialog(e)} onConfirm={confirmDeleteScreen} />

      <Dialog fullWidth open={openModel} onClose={() => { setOpenModel(false); setQuickStoreMode(false); setQuickStep(0); }}>
        <DialogTitle style={{ textAlign: "center", borderBottom: "2px solid #551fff", fontSize: '18px', fontWeight: 700 }}>Add Screen</DialogTitle>
        <DialogContent>
          {quickStoreMode ? (
            /* ===== 퀵 셋업 모드: 스토어 + 플레이리스트 + 콘텐츠 원스텝 ===== */
            <Box sx={{ pt: 2 }}>
              <Stepper activeStep={quickStep} alternativeLabel sx={{ mb: 3 }}>
                {['스토어 등록', '플레이리스트', '콘텐츠 업로드'].map(label => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>

              {quickStep === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField size="small" label="스토어 이름 *" value={quickStoreName} onChange={e => setQuickStoreName(e.target.value)} fullWidth />
                  <TextField size="small" label="TLJ No" value={quickStoreTljNo} onChange={e => setQuickStoreTljNo(e.target.value)} fullWidth />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField size="small" label="State" value={quickStoreState} onChange={e => setQuickStoreState(e.target.value)} sx={{ flex: 1 }} />
                    <TextField size="small" label="Town/City" value={quickStoreTown} onChange={e => setQuickStoreTown(e.target.value)} sx={{ flex: 1 }} />
                  </Box>
                  <TextField size="small" label="주소" value={quickStoreAddress} onChange={e => setQuickStoreAddress(e.target.value)} fullWidth />
                  <TextField size="small" label="스크린 수" type="number" value={quickStoreScreenCount} onChange={e => setQuickStoreScreenCount(Math.max(0, +e.target.value))} fullWidth inputProps={{ min: 0 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <button onClick={() => setQuickStoreMode(false)} style={{ padding: '8px 20px', border: '1px solid #ccc', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 13 }}>뒤로</button>
                    <button disabled={!quickStoreName.trim() || quickSubmitting} onClick={async () => {
                      setQuickSubmitting(true);
                      try {
                        const res = await api.post('/store/add', {
                          user: user?._id, name: quickStoreName.trim(), tljNo: quickStoreTljNo.trim(),
                          state: quickStoreState.trim(), town: quickStoreTown.trim(),
                          address: quickStoreAddress.trim(), screenCount: quickStoreScreenCount,
                        });
                        const newStore = res.data?.store;
                        if (newStore) {
                          setQuickCreatedStoreId(newStore._id);
                          setSelectedStoreId(newStore._id);
                          dispatch(getStores(user?._id));
                          toast.success(`스토어 "${quickStoreName}" 등록 완료`);
                          setQuickStep(1);
                        }
                      } catch (e) { toast.error('스토어 등록 실패: ' + (e.response?.data?.message || e.message)); }
                      setQuickSubmitting(false);
                    }} style={{ padding: '8px 24px', border: 'none', borderRadius: 6, background: '#551FFF', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: quickSubmitting ? 0.6 : 1 }}>
                      {quickSubmitting ? '등록중...' : '스토어 등록 →'}
                    </button>
                  </Box>
                </Box>
              )}

              {quickStep === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ fontSize: 13, color: '#666', textAlign: 'center' }}>플레이리스트를 바로 생성하거나, 기존 플레이리스트를 선택하세요.</div>
                  <TextField size="small" label="새 플레이리스트 이름" value={quickPlaylistName} onChange={e => setQuickPlaylistName(e.target.value)} fullWidth placeholder="예: Store Menu Board" />
                  {(playlist || []).length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 4 }}>또는 기존 플레이리스트 선택:</div>
                      <select value={quickCreatedPlaylistId} onChange={e => { setQuickCreatedPlaylistId(e.target.value); setQuickPlaylistName(''); }}
                        style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 13 }}>
                        <option value="">새로 생성</option>
                        {(playlist || []).map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                      </select>
                    </div>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <button onClick={() => setQuickStep(0)} style={{ padding: '8px 20px', border: '1px solid #ccc', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 13 }}>뒤로</button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <button onClick={() => { setQuickStep(2); }} style={{ padding: '8px 16px', border: '1px solid #551FFF', borderRadius: 6, background: 'white', color: '#551FFF', cursor: 'pointer', fontSize: 13 }}>건너뛰기</button>
                      <button disabled={(!quickPlaylistName.trim() && !quickCreatedPlaylistId) || quickSubmitting} onClick={async () => {
                        setQuickSubmitting(true);
                        try {
                          if (quickPlaylistName.trim() && !quickCreatedPlaylistId) {
                            const res = await api.post('/playlist/add', {
                              name: quickPlaylistName.trim(), user: user?._id,
                              subuser: user?._id, role: user?.role,
                            });
                            const pl = res.data?.playlist;
                            if (pl) { setQuickCreatedPlaylistId(pl._id); dispatch(getPlaylist(user?._id)); toast.success(`플레이리스트 "${quickPlaylistName}" 생성 완료`); }
                          }
                          setQuickStep(2);
                        } catch (e) { toast.error('플레이리스트 생성 실패'); }
                        setQuickSubmitting(false);
                      }} style={{ padding: '8px 24px', border: 'none', borderRadius: 6, background: '#551FFF', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: quickSubmitting ? 0.6 : 1 }}>
                        {quickSubmitting ? '처리중...' : '다음 →'}
                      </button>
                    </Box>
                  </Box>
                </Box>
              )}

              {quickStep === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ fontSize: 13, color: '#666', textAlign: 'center' }}>콘텐츠 파일을 업로드하세요. (이미지/영상)</div>
                  <input ref={quickFileRef} type="file" multiple accept="image/*,video/*" style={{ display: 'none' }}
                    onChange={e => setQuickContentFiles(Array.from(e.target.files || []))} />
                  <button onClick={() => quickFileRef.current?.click()}
                    style={{ padding: '20px', border: '2px dashed #d1d5db', borderRadius: 12, background: '#fafbff', cursor: 'pointer', fontSize: 14, color: '#551FFF', fontWeight: 600, textAlign: 'center' }}>
                    파일 선택 ({quickContentFiles.length}개 선택됨)
                  </button>
                  {quickContentFiles.length > 0 && (
                    <div style={{ maxHeight: 120, overflowY: 'auto', fontSize: 12, color: '#666' }}>
                      {quickContentFiles.map((f, i) => <div key={i}>• {f.name} ({(f.size / 1024 / 1024).toFixed(1)}MB)</div>)}
                    </div>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <button onClick={() => setQuickStep(1)} style={{ padding: '8px 20px', border: '1px solid #ccc', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 13 }}>뒤로</button>
                    <button disabled={quickSubmitting} onClick={async () => {
                      setQuickSubmitting(true);
                      try {
                        // 1) 콘텐츠 업로드
                        const uploadedContentIds = [];
                        for (const file of quickContentFiles) {
                          const form = new FormData();
                          form.append('content', file);
                          form.append('role', user?.role);
                          form.append('userId', user?.role === 'admin' ? user?._id : false);
                          form.append('subuser', user?.role === 'user' ? user?._id : false);
                          const res = await api.post('/content/upload', form);
                          if (res.data?.content?._id) uploadedContentIds.push(res.data.content._id);
                        }
                        if (uploadedContentIds.length > 0) {
                          dispatch(getContent(user?._id));
                          toast.success(`${uploadedContentIds.length}개 콘텐츠 업로드 완료`);
                        }

                        // 2) 플레이리스트에 콘텐츠 추가
                        if (quickCreatedPlaylistId && uploadedContentIds.length > 0) {
                          for (const cid of uploadedContentIds) {
                            await api.patch(`/playlist/addcontent/${quickCreatedPlaylistId}`, { content: cid });
                          }
                          dispatch(getPlaylist(user?._id));
                        }

                        // 3) 스크린 생성 + 스토어/플레이리스트 연결
                        dispatch(addScreen({
                          name: name || quickStoreName, user: user?._id,
                          subuser: user?._id, role: user?.role,
                          storeId: quickCreatedStoreId || selectedStoreId || undefined,
                        }, code));
                        emitOnAddedScreen();

                        // 스크린에 플레이리스트 연결은 생성 후 처리 필요
                        if (quickCreatedPlaylistId) {
                          setTimeout(async () => {
                            try {
                              const screensRes = await api.get(`/screen/getscreens/${user?._id}`);
                              const newScreen = (screensRes.data?.screens || []).find(s => s.code === code);
                              if (newScreen) {
                                dispatch(addPlaylistToScreen({ id: newScreen._id, value: quickCreatedPlaylistId }));
                              }
                            } catch (e) { console.error(e); }
                          }, 2000);
                        }

                        toast.success('퀵 셋업 완료!');
                        // 리셋
                        setOpenModel(false); setQuickStoreMode(false); setQuickStep(0);
                        setCode(''); setName(''); setSelectedStoreId('');
                        setQuickStoreName(''); setQuickStoreTljNo(''); setQuickStoreState('');
                        setQuickStoreTown(''); setQuickStoreAddress(''); setQuickStoreScreenCount(1);
                        setQuickPlaylistName(''); setQuickContentFiles([]);
                        setQuickCreatedStoreId(''); setQuickCreatedPlaylistId('');
                      } catch (e) { toast.error('셋업 실패: ' + (e.message || '')); }
                      setQuickSubmitting(false);
                    }} style={{ padding: '8px 24px', border: 'none', borderRadius: 6, background: '#22c55e', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: quickSubmitting ? 0.6 : 1 }}>
                      {quickSubmitting ? '처리중...' : quickContentFiles.length > 0 ? '업로드 & 완료' : '건너뛰고 완료'}
                    </button>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            /* ===== 기본 모드 ===== */
            <div className="screen-form">
              <div className="icon-screen"><AddScreenIcon /></div>
              <div className="form-fields">
                <TextField value={code} onChange={(e) => setCode(e.target.value)} type="text" variant="standard" label="Type Screen Code" fullWidth sx={{ label: { color: '#92959E', fontSize: '14px' } }} />
              </div>
              <div className="form-fields">
                <TextField value={name} onChange={(e) => setName(e.target.value)} type="text" variant="standard" label="Type Screen Name" fullWidth sx={{ label: { color: '#92959E', fontSize: '14px' } }} />
              </div>
              <div className="form-fields" style={{ marginTop: '16px' }}>
                {(stores || []).length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <p style={{ fontSize: '13px', color: '#92959E', margin: 0 }}>스토어가 없습니다.</p>
                    <button onClick={() => setQuickStoreMode(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '2px dashed #551FFF', borderRadius: 10, background: 'rgba(85,31,255,0.04)', cursor: 'pointer', color: '#551FFF', fontSize: 13, fontWeight: 700 }}>
                      <AddBusinessIcon style={{ fontSize: 18 }} /> 퀵 스토어 등록 →
                    </button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <select value={selectedStoreId} onChange={e => setSelectedStoreId(e.target.value)}
                      style={{ width: '100%', height: '40px', border: '0', borderBottom: '1px solid rgba(0,0,0,0.42)', fontSize: '14px', color: selectedStoreId ? '#15192C' : '#92959E', background: 'transparent', outline: 'none', cursor: 'pointer' }}>
                      <option value="">스토어 선택 (선택 사항)</option>
                      {(stores || []).map(st => (
                        <option key={st._id} value={st._id}>
                          {st.name}{st.town ? ` — ${st.state || ''} ${st.town}`.trim() : st.address ? ` — ${st.address}` : ''}
                        </option>
                      ))}
                    </select>
                    <button onClick={() => setQuickStoreMode(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', border: '1px dashed #551FFF', borderRadius: 8, background: 'transparent', cursor: 'pointer', color: '#551FFF', fontSize: 12, fontWeight: 600, alignSelf: 'flex-start' }}>
                      <AddBusinessIcon style={{ fontSize: 14 }} /> 새 스토어 등록
                    </button>
                  </Box>
                )}
              </div>
              <Box sx={{ display: 'flex', mt: 6, mb: 4, justifyContent: 'center', gap: 2 }}>
                <button style={{ background: 'white', padding: '8px', width: '130px', color: '#551FFF', border: '1px solid #551FFF', borderRadius: '5px' }} onClick={() => setOpenModel(false)}>Cancel</button>
                <button style={{ background: '#551FFF', padding: '8px', width: '130px', color: 'white', border: 'none', borderRadius: '5px' }} onClick={submit}>Save</button>
              </Box>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Device Dialog */}
      <Dialog fullWidth maxWidth="xs" open={openAssignDevice} onClose={() => { setOpenAssignDevice(false); setDeviceCode(""); setAssignTargetScreen(null); }}>
        <DialogTitle style={{ textAlign: 'center', borderBottom: '2px solid #551fff', fontSize: '18px', fontWeight: 700 }}>기기 연결</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {assignTargetScreen && (
              <p style={{ fontSize: '13px', color: '#92959E', textAlign: 'center', margin: 0 }}>
                <strong style={{ color: '#15192C' }}>{assignTargetScreen.name}</strong> 스크린에 연결할 기기 코드를 입력하세요.
              </p>
            )}
            <TextField
              value={deviceCode}
              onChange={e => setDeviceCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitAssignDevice(); }}
              type="text"
              variant="standard"
              label="기기 코드 (TV 앱에서 제공)"
              fullWidth
              autoFocus
              sx={{ label: { color: '#92959E', fontSize: '14px' } }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <button onClick={() => { setOpenAssignDevice(false); setDeviceCode(""); setAssignTargetScreen(null); }}
                style={{ background: 'white', padding: '8px', width: '130px', color: '#551FFF', border: '1px solid #551FFF', borderRadius: '5px', cursor: 'pointer' }}>
                취소
              </button>
              <button onClick={submitAssignDevice} disabled={!deviceCode.trim()}
                style={{ background: deviceCode.trim() ? '#551FFF' : '#ccc', padding: '8px', width: '130px', color: 'white', border: 'none', borderRadius: '5px', cursor: deviceCode.trim() ? 'pointer' : 'default' }}>
                연결
              </button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog fullWidth open={openBulkDialog} onClose={() => { setOpenBulkDialog(false); setPlaylistSearch(""); }}>
        <DialogTitle style={{ textAlign: 'center', borderBottom: '2px solid #551fff', fontSize: '18px', fontWeight: 700 }}>플레이리스트 일괄 변경</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <p style={{ fontSize: '13px', color: '#92959E', textAlign: 'center', margin: 0 }}>선택된 {checkedIds.size}개 스크린에 적용할 플레이리스트를 선택하세요.</p>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input value={playlistSearch} onChange={e => setPlaylistSearch(e.target.value)} placeholder="이름 검색..."
                style={{ flex: 1, height: '36px', border: '1px solid rgba(85,31,255,0.3)', borderRadius: '8px', padding: '0 10px', fontSize: '13px', outline: 'none' }} />
              <select value={playlistSort} onChange={e => setPlaylistSort(e.target.value)}
                style={{ height: '36px', border: '1px solid rgba(85,31,255,0.3)', borderRadius: '8px', padding: '0 8px', fontSize: '12px', color: '#555' }}>
                <option value="name-asc">이름 A→Z</option>
                <option value="name-desc">이름 Z→A</option>
                <option value="date-desc">최신순</option>
                <option value="date-asc">오래된순</option>
              </select>
            </Box>
            <Box sx={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid rgba(85,31,255,0.15)', borderRadius: '8px' }}>
              {getFilteredPlaylists().length === 0
                ? <p style={{ textAlign: 'center', color: '#aaa', fontSize: '13px', padding: '20px' }}>결과 없음</p>
                : getFilteredPlaylists().map(p => (
                  <Box key={p._id} onClick={() => setBulkPlaylistId(p._id)}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, cursor: 'pointer', borderBottom: '1px solid rgba(0,0,0,0.05)',
                      background: bulkPlaylistId === p._id ? 'rgba(85,31,255,0.08)' : 'white', '&:hover': { background: 'rgba(85,31,255,0.05)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #551FFF', background: bulkPlaylistId === p._id ? '#551FFF' : 'white', flexShrink: 0 }} />
                      <span style={{ fontSize: '14px', color: '#15192C' }}>{p.name}</span>
                    </Box>
                    <span style={{ fontSize: '11px', color: '#aaa' }}>{p.content?.filter(c => c.type === 'video').length}개 영상</span>
                  </Box>
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <button onClick={() => { setOpenBulkDialog(false); setPlaylistSearch(""); }} style={{ background: 'white', padding: '8px', width: '130px', color: '#551FFF', border: '1px solid #551FFF', borderRadius: '5px', cursor: 'pointer' }}>취소</button>
              <button onClick={applyBulkPlaylist} disabled={!bulkPlaylistId} style={{ background: bulkPlaylistId ? '#551FFF' : '#ccc', padding: '8px', width: '130px', color: 'white', border: 'none', borderRadius: '5px', cursor: bulkPlaylistId ? 'pointer' : 'default' }}>적용</button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      {/* OTA Update Dialog */}
      <Dialog open={otaDialog} onClose={() => setOtaDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>OTA Update</DialogTitle>
        <DialogContent>
          <p style={{ margin: '8px 0' }}>
            Send latest APK to <strong>{otaTarget?.name || otaTarget?.mac || 'selected device'}</strong>?
          </p>
          <p style={{ fontSize: 12, color: '#888' }}>
            Current version: {otaTarget?.appVersion || 'unknown'}<br />
            Target: v2.13 (latest)
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <button onClick={() => setOtaDialog(false)}
              style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={() => sendOtaUpdate([otaTarget?.mac || otaTarget?.code])}
              disabled={otaSending}
              style={{ padding: '8px 16px', background: '#2196F3', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: otaSending ? 0.5 : 1 }}>
              {otaSending ? 'Sending...' : 'Send OTA'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* OTA History Dialog */}
      <Dialog open={otaHistoryDialog} onClose={() => setOtaHistoryDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Command History — {otaHistoryCode}</DialogTitle>
        <DialogContent>
          {otaHistory.length === 0 ? (
            <p style={{ color: '#888' }}>No commands found</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Command</th>
                  <th style={{ padding: 8 }}>Status</th>
                  <th style={{ padding: 8 }}>Version</th>
                  <th style={{ padding: 8 }}>Sent</th>
                  <th style={{ padding: 8 }}>By</th>
                </tr>
              </thead>
              <tbody>
                {otaHistory.map((cmd, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 8 }}>{cmd.command}</td>
                    <td style={{ padding: 8 }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: cmd.status === 'acked' ? '#e8f5e9' : cmd.status === 'delivered' ? '#e3f2fd' : cmd.status === 'pending' ? '#fff3e0' : '#fce4ec',
                        color: cmd.status === 'acked' ? '#2e7d32' : cmd.status === 'delivered' ? '#1565c0' : cmd.status === 'pending' ? '#e65100' : '#c62828',
                      }}>
                        {cmd.status}
                      </span>
                    </td>
                    <td style={{ padding: 8, color: '#666' }}>{cmd.payload?.version || '—'}</td>
                    <td style={{ padding: 8, color: '#666' }}>{cmd.createdAt ? new Date(cmd.createdAt).toLocaleString() : '—'}</td>
                    <td style={{ padding: 8, color: '#666' }}>{cmd.createdBy || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Screen;
